(() => {
  'use strict';

  if (window.__fereLeadAnalyticsInstalled) return;
  window.__fereLeadAnalyticsInstalled = true;

  const PENDING_TTL_MS = 30 * 60 * 1000;
  const CONFIGS = [
    {
      source: 'gold_trade',
      pathname: '/pages/sell-your-gold',
      formId: 'FereSellGoldForm',
      successSelector: '[data-fere-gold-success]',
    },
    {
      source: 'custom_ring_builder',
      pathname: '/pages/custom',
      formId: 'FereCustomRingRequestForm',
      successSelector: '[data-fcrb-success], [data-fere-native-success]',
      resultFormId: 'FereConsultationResultState',
    },
  ];

  const storage = (() => {
    try {
      const key = '__fere_lead_storage_test__';
      window.sessionStorage.setItem(key, '1');
      window.sessionStorage.removeItem(key);
      return window.sessionStorage;
    } catch (_error) {
      return null;
    }
  })();

  const pendingKey = (source) => `fere:lead:pending:${source}`;
  const consumedKey = (source) => `fere:lead:consumed:${source}`;

  const parseStoredAttempt = (source) => {
    if (!storage) return null;
    try {
      const value = JSON.parse(storage.getItem(pendingKey(source)) || 'null');
      if (
        !value
        || value.source !== source
        || typeof value.token !== 'string'
        || !value.token
        || !Number.isFinite(value.createdAt)
        || Date.now() - value.createdAt > PENDING_TTL_MS
        || value.createdAt > Date.now() + 60_000
      ) {
        storage.removeItem(pendingKey(source));
        return null;
      }
      return value;
    } catch (_error) {
      storage.removeItem(pendingKey(source));
      return null;
    }
  };

  const makeToken = () => {
    try {
      if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
      const bytes = new Uint32Array(4);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (value) => value.toString(36)).join('-');
    } catch (_error) {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
  };

  const armAttempt = (source) => {
    if (!storage) return;
    storage.setItem(pendingKey(source), JSON.stringify({
      source,
      token: makeToken(),
      createdAt: Date.now(),
    }));
  };

  const navigationType = () => {
    try {
      return window.performance.getEntriesByType('navigation')[0]?.type || 'navigate';
    } catch (_error) {
      return 'navigate';
    }
  };

  const publish = (source) => {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return Promise.resolve(false);
    try {
      return Promise.resolve(
        window.Shopify.analytics.publish('generate_lead', { lead_source: source }),
      ).then((result) => result !== false, () => false);
    } catch (_error) {
      return Promise.resolve(false);
    }
  };

  const emitConfirmedSuccess = async (config) => {
    if (!storage || navigationType() !== 'navigate') return false;
    if (window.location.pathname.replace(/\/$/, '') !== config.pathname) return false;
    if (new URLSearchParams(window.location.search).get('contact_posted') !== 'true') return false;

    const form = (document.getElementById(config.formId) || (config.resultFormId && document.getElementById(config.resultFormId)));
    if (!form || !form.querySelector(config.successSelector)) return false;

    const attempt = parseStoredAttempt(config.source);
    if (!attempt || storage.getItem(consumedKey(config.source)) === attempt.token) return false;

    storage.setItem(consumedKey(config.source), attempt.token);
    storage.removeItem(pendingKey(config.source));

    for (let count = 0; count < 40; count += 1) {
      if (await publish(config.source)) return true;
      await new Promise((resolve) => window.setTimeout(resolve, 125));
    }

    storage.removeItem(consumedKey(config.source));
    storage.setItem(pendingKey(config.source), JSON.stringify(attempt));
    return false;
  };

  const emitCustomRingMetaLead = () => {
    if (!storage || navigationType() !== 'navigate' || window.Shopify?.designMode) return;
    const form = document.getElementById('FereCustomRingRequestForm')
      || document.getElementById('FereConsultationResultState');
    if (!form?.querySelector('[data-fere-native-success]')
      || document.querySelector('[data-fcrb-form-errors], [data-fere-booking-error]')) return;

    let receipt;
    let token;
    const sentKey = 'fere:meta:lead:custom_ring_builder';
    try {
      const url = new URL(window.location.href);
      if (!/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?pages\/custom\/?$/.test(url.pathname)) return;
      receipt = JSON.parse(storage.getItem('fere_custom_ring_submission_v3') || 'null');
      token = receipt?.requestId;
      const requestMarker = url.searchParams.get('fcrb_request');
      const receiptMarker = url.searchParams.get('fcrb_receipt');
      const age = Date.now() - Number(receipt?.savedAt);
      if (!/^[a-zA-Z0-9-]{12,80}$/.test(String(token || ''))
        || !['pending', 'confirmed'].includes(receipt?.status)
        || receipt.returnPath !== url.pathname
        || !Number.isFinite(age) || age < 0 || age >= 60 * 60 * 1000
        || (requestMarker && receiptMarker && requestMarker !== receiptMarker)
        || (requestMarker || receiptMarker) !== token
        || storage.getItem(sentKey) === token) return;
    } catch (_error) { return; }

    let stopped = false;
    let retryTimer = null;
    let attempts = 0;
    const stop = () => {
      stopped = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      document.removeEventListener('visitorConsentCollected', trySend);
      window.removeEventListener('pagehide', stop);
    };
    const consentAllowsMeta = () => {
      const privacy = window.Shopify?.customerPrivacy;
      try {
        return typeof privacy?.marketingAllowed === 'function'
          && privacy.marketingAllowed() === true
          && typeof privacy?.analyticsProcessingAllowed === 'function'
          && privacy.analyticsProcessingAllowed() === true
          && typeof privacy?.saleOfDataAllowed === 'function'
          && privacy.saleOfDataAllowed() === true;
      } catch (_error) { return false; }
    };
    const trySend = () => {
      if (stopped) return;
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (Date.now() - Number(receipt.savedAt) >= 60 * 60 * 1000) {
        stop();
        return;
      }
      if (consentAllowsMeta() && typeof window.fbq === 'function') {
        try {
          if (storage.getItem(sentKey) === token) { stop(); return; }
          storage.setItem(sentKey, token);
          window.fbq('trackSingle', '1094514923037986', 'Lead');
          stop();
          return;
        } catch (_error) {
          try { storage.removeItem(sentKey); } catch (_storageError) { stop(); return; }
        }
      }
      attempts += 1;
      if (attempts < 40) retryTimer = window.setTimeout(trySend, 125);
    };
    document.addEventListener('visitorConsentCollected', trySend);
    window.addEventListener('pagehide', stop, { once: true });
    if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (!error) trySend();
        });
      } catch (_error) {}
    }
    trySend();
  };

  const init = () => {
    emitCustomRingMetaLead();
    CONFIGS.forEach((config) => {
      const form = (document.getElementById(config.formId) || (config.resultFormId && document.getElementById(config.resultFormId)));
      const success = form?.querySelector(config.successSelector);

      if (success) {
        void emitConfirmedSuccess(config);
        return;
      }

      if (!form) return;
      if (storage) storage.removeItem(pendingKey(config.source));

      form.addEventListener('submit', (event) => {
        queueMicrotask(() => {
          if (event.defaultPrevented || !form.checkValidity()) return;
          armAttempt(config.source);
        });
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
